import { useHeaderOverlay } from '../lib/HeaderOverlayContext';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useTheme } from '../lib/ThemeContext';
import { useOverlayFocus } from '../lib/useOverlayFocus';
import { useWorkspacePath } from '../lib/workspaceRoutes';
import { ActiveRaterLabel, ActiveRaterPicker } from './ActiveRaterPicker';
import { useMe } from '../lib/useMe';
import { useWorkspace } from '../lib/WorkspaceContext';

/**
 * Avatar + dropdown in the top-right. Self-contained — no library.
 *   - click the avatar to toggle
 *   - outside-click or Escape closes
 *   - admin status shown as a signature-green ring around the avatar +
 *     a badge row in the menu
 */
export function UserMenu() {
  const auth = useAuth();
  const { workspaceId } = useWorkspace();
  const me = useMe(workspaceId);
  const isAdmin = me.data?.isAdmin === true;
  const isSuperAdmin = me.data?.isSuperAdmin === true;
  const { theme, toggle: toggleTheme } = useTheme();
  const workspaceTo = useWorkspacePath();
  const [open, setOpen] = useHeaderOverlay('user');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useOverlayFocus({
    open,
    panelRef,
    triggerRef,
    preserveExternalFocus: true,
    onClose: () => setOpen(false),
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, setOpen]);

  if (!auth.email) return null;
  const localPart = auth.email.split('@')[0] ?? '';
  const displayName = formatDisplayName(localPart);

  return (
    <div ref={ref} className="relative flex min-w-0 items-center gap-2 max-[700px]:flex-col-reverse max-[700px]:gap-0">
      <ActiveRaterLabel />
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu for ${displayName}`}
        className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] p-0.5 pr-2 text-white transition-colors hover:bg-white/[0.10]"
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.14] text-[length:var(--type-data)] font-semibold tracking-wide text-white ring-1 ring-white/20 transition-colors"
        >
          {initials(localPart)}
        </span>
        <svg
          viewBox="0 0 12 8"
          className={`h-2.5 w-2.5 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          aria-hidden
        >
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-20 w-72 overflow-hidden rounded-lg border border-[var(--color-brand-line)] bg-[var(--color-brand-surface)] shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
        >
          <div className="px-4 py-3">
            <div className="truncate text-sm font-medium text-white">{displayName}</div>
            <div className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">{auth.email}</div>
            {isAdmin && (
              <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-[var(--color-brand-green)]/35 bg-[var(--color-brand-green)]/10 px-2 py-0.5 text-[length:var(--type-data)] text-[var(--color-brand-green)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-brand-green)]" aria-hidden />
                {isSuperAdmin ? 'Superadmin' : 'Admin'}
              </div>
            )}
          </div>
          {isSuperAdmin && (
            <>
              <div className="border-t border-white/[0.06]" />
              <div className="px-4 py-3">
                <div className="mb-2 text-[length:var(--type-data)] text-[var(--text-secondary)]">
                  Logged in as
                </div>
                <ActiveRaterPicker className="w-full" />
              </div>
            </>
          )}
          <div className="border-t border-white/[0.06]" />
          <button
            type="button"
            role="menuitem"
            onClick={toggleTheme}
            className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            <span className="flex items-center gap-2">
              {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
              {theme === 'dark' ? 'Dark' : 'Light'} mode
            </span>
            <span
              aria-hidden
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                theme === 'light' ? 'bg-[var(--color-brand-green)]/60' : 'bg-white/15'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  theme === 'light' ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
          <div className="border-t border-white/[0.06]" />
          {isAdmin && (
            <Link
              to={workspaceTo('/admin')}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm text-white/75 transition-colors hover:bg-white/[0.04] hover:text-white"
            >
              Admin
            </Link>
          )}
          <div className="border-t border-white/[0.06]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              auth.logout();
            }}
            className="block w-full px-4 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <path
        d="M13 9.3A5.2 5.2 0 0 1 6.7 3a5.5 5.5 0 1 0 6.3 6.3Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
      <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
        <path d="M8 1v1.6M8 13.4V15M1 8h1.6M13.4 8H15M3.05 3.05l1.13 1.13M11.82 11.82l1.13 1.13M3.05 12.95l1.13-1.13M11.82 4.18l1.13-1.13" />
      </g>
    </svg>
  );
}

function initials(localPart: string): string {
  const parts = localPart.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  return (localPart.slice(0, 2) || '?').toUpperCase();
}

function formatDisplayName(localPart: string): string {
  const parts = localPart.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return localPart;
  return parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
}
